import { create } from "zustand";

import { EventDetail, EventTiny } from "@/types/event";
import { QuickNoteSummary } from "@/types/quick-note";
import { UserTiny } from "@/types/user";

import { onTenantChange, useTenantStore } from "./tenant-store";

export interface MemoWindowRequest {
  patient: UserTiny;
  event?: EventTiny | EventDetail;
  existingNote?: QuickNoteSummary;
}

interface OpenMemoWindow extends MemoWindowRequest {
  /** The clinic the window was opened in; it only ever shows there. */
  subDomain: string | null;
}

interface MemoWindowStore {
  request: OpenMemoWindow | null;
  /** Bumped on every new request so the window starts with a fresh form. */
  requestKey: number;
  openMemoWindow: (request: MemoWindowRequest) => void;
  closeMemoWindow: () => void;
}

/**
 * The memo window doesn't lock the page, so staff can move between chart
 * sections or to another page while writing. It is rendered once at the
 * console layout (GlobalMemoWindow) and opened through this store, so the
 * window and its draft outlive whichever component opened it.
 */
export const useMemoWindowStore = create<MemoWindowStore>((set, get) => ({
  request: null,
  requestKey: 0,
  openMemoWindow: (request) => {
    const current = get().request;
    // Asking again for the window that is already open (the same patient's
    // "Add Memo", or "Edit" on the same saved memo) keeps the draft.
    if (
      current &&
      current.existingNote?.id === request.existingNote?.id &&
      current.patient.id === request.patient.id &&
      current.event?.id === request.event?.id
    ) {
      return;
    }
    set((state) => ({
      request: {
        ...request,
        subDomain: useTenantStore.getState().getCurrentTenant(),
      },
      requestKey: state.requestKey + 1,
    }));
  },
  closeMemoWindow: () => set({ request: null }),
}));

// A draft about one clinic's patient must never follow the user into another
// clinic. The window itself remounts on a clinic switch, so this lives with the
// store, which doesn't.
onTenantChange(() => useMemoWindowStore.getState().closeMemoWindow());
